<template>
  <v-row>
    <v-col cols="12">
      <v-card class="px-0 mt-5">
        <v-skeleton-loader v-if="$store.state.subscriptions.retrieveLoader" transition="fade-transition" type="toolbar" />
        <v-toolbar v-else color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Registros de uso</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-query-builder :query-map="filters" model="subscription" class="mr-1" />
              <v-btn @click="dialogImport=true" outlined color="secondary"><v-icon left small>mdi-plus</v-icon>Importar</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="px-0 py-0">
          <usage-list />
        </v-card-text>
      </v-card>
    </v-col>

    <!-- dialog import -->
    <v-dialog v-model="dialogImport" no-click-animation persistent scrollable width="800">
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Importar registros de uso</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="ml-3" @click="dialogImport=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <template v-if="importStatus">
            <!-- import status -->
            <v-row class="mx-0">
              <v-col cols="12" class="white">
                <v-list class="transparent">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Estado de la importación</v-list-item-title>
                      <v-list-item-subtitle class="mt-1">
                        <code :class="{'text--secondary background': importStatus === 'success'}">{{importStatus}}</code>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Consumos importados</v-list-item-title>
                      <v-list-item-subtitle class="mt-1">{{count}}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
            <!-- end import status -->

            <!-- issues -->
            <template v-if="importIssues">
              <v-divider />
              <v-toolbar color="transparent" flat height="55">
                <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Corrige los siguientes problemas en el archivo y vuelve a intentar la importación</v-toolbar-title>
              </v-toolbar>
              <v-row class="mx-0">
                <v-timeline dense>
                  <v-timeline-item v-for="(issue, key) in importIssues" :key="key" color="secondary" fill-dot right small>
                    <v-card class="elevation-1">
                      <v-card-title class="body-2 text--secondary font-weight-medium pb-0 pt-3">Número de fila ({{key}})</v-card-title>
                      <v-card-text class="pb-3">
                        <template v-for="fields in issue">
                          <template v-for="(errors, field) in fields">
                            <span class="d-block mt-2 font-weight-medium" :key="`key-${field}`">Campo {{field}}</span>
                            <span class="mb-2 text--primary" :key="`error-${field}`">{{errors.join(' ')}}</span>
                          </template>
                        </template>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </v-row>
            </template>
            <!-- end issues -->
          </template>

          <!-- instructions & import -->
          <template v-else>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <v-list class="transparent" dense>
                  <v-list-item class="pt-4">
                    <v-list-item-icon><v-avatar color="indigo" size="24"><span class="white--text">1</span></v-avatar></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2 text--secondary font-weight-medium">Descarga el archivo base de importación</v-list-item-title>
                      <v-list-item-subtitle><v-btn @click="exportBaseFile" :loading="$store.state.accounts.createLoader" text color="primary" :ripple="false"><v-icon color="primary" small left>mdi-download</v-icon>Descargar</v-btn></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="pt-2">
                    <v-list-item-icon><v-avatar color="indigo" size="24"><span class="white--text">2</span></v-avatar></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2 text--secondary font-weight-medium">Completa las columnas <span class="text--secondary font-weight-medium">date_time y quantity</span></v-list-item-title>
                      <v-list-item-subtitle class="mt-2 font-weight-regular text--primary">Usa date_time para la fecha y hora del registro (AAAA-MM-DDThh:mm) y quantity para la cantidad del registro</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="pt-4">
                    <v-list-item-icon><v-avatar color="indigo" size="24"><span class="white--text">3</span></v-avatar></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2 text--secondary font-weight-medium">Importa los registros</v-list-item-title>
                      <v-list-item-subtitle class="mt-2 font-weight-regular text--primary">Selecciona un archivo en formato <span class="text--scondary">XLSX, CSV o JSON</span></v-list-item-subtitle>
                      <v-list-item-subtitle class="mt-2 font-weight-regular text--primary">
                        <v-file-input v-model="file" type="file" accept=".csv,.xlsx,.json" :error="importFormatIsValid" prepend="mdi-paper-clip" background-color="transparent" hide-details placeholder="Seleccionar archivo" solo flat chips />
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </template>
          <!-- end instructions & import -->
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <template v-if="importStatus">
            <v-btn @click="dialogImport=false, clearImport()" outlined>Salir</v-btn>
            <v-btn @click="clearImport"  color="primary">Nueva importación</v-btn>
          </template>
          <template v-else>
            <v-btn @click="dialogImport=false, clearImport()" outlined>Cancelar</v-btn>
            <v-btn @click="createImport" :loading="loaderImport" :disabled="!file" color="primary">Importar</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog import -->
  </v-row>
</template>
<script>
import { saveAs } from 'file-saver'
import { usagesFilters } from '../../collections/queryBuilderFilters'

// components
import UsageList from '../../components/subscription/UsageList'
import VQueryBuilder from '../../components/VQueryBuilder/VQueryBuilder'

// mixins
import ListViewMixin from '../../mixins/ListViewMixin'

export default {
  components: {
    UsageList,
    VQueryBuilder
  },
  mixins: [
    ListViewMixin
  ],
  data: () => ({
    count: null,
    file: null,
    dialogImport: false,
    filters: usagesFilters,
    importFormat: '',
    importFormatIsValid: false,
    importFormats: ['csv', 'xlsx', 'json'],
    importIssues: null,
    importStatus: null,
    loaderImport: false
  }),
  methods: {
    clearImport () {
      this.count = null
      this.file = null
      this.importStatus = null
      this.importIssues = null
    },
    createImport () {
      if (this.file == null) {
        return false
      }

      this.loaderImport = true
      let array = this.file.name.split('.')
      this.importFormat = array[array.length - 1]

      if (!this.importFormats.includes(this.importFormat)) {
        this.importFormatIsValid = true
      }

      const payload = new FormData()
      payload.append('file', this.file)
      payload.append('import_format', this.importFormat)

      this.$store.dispatch('accounts/CREATE', {
        resource: 'imports/usages',
        payload: payload,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => {
        this.importStatus = response.data.status
        this.count = response.data.count
        this.importIssues = response.data.issues

        if (this.importStatus === 'success') {
          this.getList(false)
        }
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loaderImport = false
      })
    },
    exportBaseFile () {
      this.$store.dispatch('accounts/LIST', {
        resource: 'exports/subscription_line_usages',
        query: this.$route.query,
        loaderImport: true,
        responseType: 'arraybuffer'
      })
      .then(async (response) => {
        let blob = await new Blob([response.data], { type: 'application/zip' })
        saveAs(blob, 'usages.zip')
        this.$dialog.message.info('La exportación se realizó con éxito.')
      })
      .catch(() => {
        this.$dialog.message.error('Ha ocurrido un error en la exportación.')
      })
    },
    getList (loader = true) {
      this.$store.dispatch('subscriptions/LIST', {
        resource: 'usages',
        query: {
          expand: 'subscription_line',
          ...this.$route.query
        },
        loader: loader
      })
    }
  }
}
</script>
